import { useEffect, useMemo, useState } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { CommonDatagrid } from '../../components/DataGrid';
import { ViewButton } from '../../components/Buttons/ViewButton';
import { RefreshButton } from '../../components/Buttons/RefreshButton';
import { PackPickService } from '../../api/services/packPickService';
import { IPendingPackOrder } from '../../types/api/response/packpickresponse';
import { PickPackDialog } from './PickPackDialog';

const STATUS_COLOR: Record<
  string,
  'default' | 'warning' | 'info' | 'success' | 'error' | 'primary'
> = {
  APPROVED: 'warning',
  PICKING: 'info',
  PACKING: 'primary',
  PACKING_CANCELLED: 'error',
};

export const OrdersToPickPage = () => {
  const [orders, setOrders] = useState<IPendingPackOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<IPendingPackOrder | null>(null);
  const [isOpenDialog, setIsOpenDialog] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await PackPickService.getStatusQueues();
      setOrders(res.pending ?? []);
    } catch {
      // error handled silently — could dispatch notification if needed
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onClickView = (order: IPendingPackOrder) => {
    setSelectedOrder(order);
    setIsOpenDialog(true);
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'order_no',
        headerName: 'Order No',
        flex: 1,
        minWidth: 160,
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 0.6,
        minWidth: 130,
        renderCell: (params: GridRenderCellParams) => (
          <Chip
            label={params.row.status}
            color={STATUS_COLOR[params.row.status] ?? 'default'}
            size="small"
          />
        ),
      },
      {
        field: 'delivery_type',
        headerName: 'Delivery Type',
        flex: 0.6,
        minWidth: 130,
      },
      {
        field: 'delivery_slot',
        headerName: 'Delivery Slot',
        flex: 0.8,
        minWidth: 180,
        renderCell: (params: GridRenderCellParams) => (
          <Typography variant="body2">
            {params.row.delivery_slot?.start_time} - {params.row.delivery_slot?.end_time}
          </Typography>
        ),
      },
      {
        field: 'cart_item_count',
        headerName: 'Items',
        flex: 0.4,
        minWidth: 90,
      },
      {
        field: 'actions',
        headerName: '',
        minWidth: 100,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => (
          <ViewButton onClick={() => onClickView(params.row as IPendingPackOrder)} />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={3}>
        Orders to Pick
      </Typography>

      <CommonDatagrid
        data={orders}
        columns={columns}
        rowId="order_no"
        searchKey="order_no"
        loading={loading}
        searchbarPlaceholder="Search by order number..."
        headerActionButtons={<RefreshButton onClick={() => load()} />}
      />

      {selectedOrder && (
        <PickPackDialog
          orderNumber={selectedOrder.order_no}
          isOpenDialog={isOpenDialog}
          setIsOpenDialog={setIsOpenDialog}
          onCompleted={() => load()}
        />
      )}
    </Box>
  );
};
